import { useMemo, useState } from 'react';
import { Button, Flex, Spin } from 'antd';
import { FrameworkExecution } from '../../../../../features/frameworks/type';
import { FrameworkCard } from '../../../../../features/frameworks/components/Card';
import { useToast } from '../../../../../hooks/useToast';
import { FrameworksHeader } from '../../../../../features/frameworks/components/FrameworksHeader';
import { coreService } from '../../../../../services/core/core-service';
import { format } from 'date-fns';
import { useAccountOverview } from '../../../../../features/account/account-overview/account-overview.hook';
import { capitalize } from 'lodash';

import Styles from './styles';
import { Alert } from '../../../../../components/alerts/Alert';
import { formatDate, formatInsightTimeWithYear } from '../../../../../utils/dateUtils';
const { Container, EmptyView, Cards } = Styles;

interface InternalFrameworkProps {
  lastExecution: FrameworkExecution | null;
  selectedFrameworkName: string | null;
  loading: boolean;
  onGenerate: () => void;
  updateLastExecution: (args: { id: string; value: string }) => void;
}

const InternalFramework: React.FC<InternalFrameworkProps> = ({
  lastExecution,
  loading,
  selectedFrameworkName,
  onGenerate,
  updateLastExecution,
}) => {
  const context = useMemo(() => {
    if (
      lastExecution &&
      typeof lastExecution.context === 'object' &&
      lastExecution.context.from &&
      lastExecution.context.to
    ) {
      let defaultText = `• ${selectedFrameworkName} executed on ${formatInsightTimeWithYear(
        lastExecution.createdAt,
      )}\n• Includes meetings from ${formatDate(lastExecution.context.from)} to ${formatDate(
        lastExecution.context.to,
      )}\n• External meeting types included: `;
      if (
        Array.isArray(lastExecution.context.meetingType) &&
        lastExecution.context.meetingType.length > 0
      ) {
        defaultText += lastExecution.context.meetingType.join(', ');
      } else {
        defaultText += 'All';
      }
      return {
        hasContext: true,
        text: defaultText,
      };
    }

    return {
      hasContext: false,
    };
  }, [lastExecution]);
  if (loading) {
    return (
      <Container>
        <Cards style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin />
        </Cards>
      </Container>
    );
  }

  if (!lastExecution?.id)
    return (
      <Container>
        <Cards style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyView
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <span style={{ width: '500px', textAlign: 'center' }}>
              Generate a customer intelligence report using the '{selectedFrameworkName || ''}'
              framework.
            </span>
            <Button onClick={onGenerate} type="primary">
              Generate
            </Button>
          </EmptyView>
        </Cards>
      </Container>
    );

  if (lastExecution?.isProcessing) {
    return (
      <Container>
        <Cards style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyView
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}
          >
            <span style={{ width: '500px', textAlign: 'center' }}>
              Your framework report is being generated and will be available momentarily. You'll
              receive an email when it's ready.
            </span>
            <Spin />
          </EmptyView>
        </Cards>
      </Container>
    );
  }

  return (
    <Cards>
      {context.hasContext && (
        <Alert
          variant="info"
          title=""
          message={
            <Flex vertical gap={4}>
              <span style={{ whiteSpace: 'pre-line' }}>{context.text}</span>
            </Flex>
          }
        />
      )}
      {lastExecution &&
        lastExecution?.values.map(v => (
          <FrameworkCard
            key={'fw-card-qst-id' + v.questionId}
            id={v.questionId}
            title={v.question}
            value={v.answer}
            loading={loading}
            onSave={updateLastExecution}
          />
        ))}
    </Cards>
  );
};

export const Frameworks: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [lastExecution, setLastExecution] = useState<FrameworkExecution | null>(null);
  const id = window.location.pathname.replace('/accounts/', '');

  const { selectedAccount } = useAccountOverview();
  const { success } = useToast();

  if (lastExecution === undefined) {
    return (
      <Container
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '600px' }}
      >
        <EmptyView
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}
        >
          <Spin />
        </EmptyView>
      </Container>
    );
  }

  const updateLastExecution = (args: { id: string; value: string }) => {
    if (!lastExecution) return;
    const updatedExecution = {
      ...lastExecution,
      values: lastExecution.values.map(v =>
        v.questionId === args.id ? { ...v, answer: args.value } : v,
      ),
    };
    setLastExecution(updatedExecution);
    setLoading(true);
    coreService.updateAccountFrameworkExecution(id, updatedExecution).finally(() => {
      setLoading(false);
    });
  };

  return (
    <Container>
      <FrameworksHeader
        showContext
        setLastExecution={setLastExecution}
        fetchLastExecution={externalId => {
          return coreService.fetchAccountFrameworksByExternalId(id, externalId);
        }}
        onGenerate={(externalId, context) => {
          return coreService.generateAccountFrameworksByExternalId(id, externalId, context);
        }}
        onCopy={(name, createdAt) => {
          navigator.clipboard.writeText(
            `Account: ${capitalize(selectedAccount.name)}\n\n${name} - ${format(
              new Date(createdAt),
              'MMMM dd, yyyy hh:mm aa',
            )}\n\n${lastExecution?.values.map(v => `${v.question}:\n ${v.answer}`).join('\n\n')}`,
          );
          success('Copied to clipboard');
        }}
        disabled={!!lastExecution?.isProcessing || loading}
        key={'accounts-frameworks-header'}
      >
        <InternalFramework
          loading={loading}
          selectedFrameworkName={null}
          lastExecution={lastExecution}
          onGenerate={() => 'generate'}
          updateLastExecution={updateLastExecution}
        />
      </FrameworksHeader>
    </Container>
  );
};
